/* eslint-disable max-classes-per-file */

import Enum from '@/modules/core/app/models/abstracts/Enum';
import {
  CITY,
  CONGRESSIONAL_DISTRICT,
  COUNTRY,
  COUNTY,
  DMA,
  LAT_LONG,
  STATE,
  ZIP_CODE,
} from '@/modules/ta/widget/mapbox.constants';

export class WidgetType extends Enum {
  static TYPE_BIGNUMBER = 'bignumber';

  static TYPE_DATAGRID = 'datagrid';

  static TYPE_TOPPERFORMER = 'topperformer';

  static TYPE_BARCHART = 'barchart';

  static TYPE_BUBBLECHART = 'bubblechart';

  static TYPE_COMBINATIONCHART = 'combinationchart';

  static TYPE_LINECHART = 'linechart';

  static TYPE_PIECHART = 'piechart';

  static TYPE_GEOCHART = 'geochart';

  static TYPE_GAUGECHART = 'gaugechart';

  static TYPE_FUNNELCHART = 'funnelchart';

  static TYPE_MEDIA = 'media';

  static TYPE_ACCOUNTMANAGER = 'accountmanager';

  static TYPE_GOAL = 'goal';

  static TYPE_GOALCHART = 'goalchart';

  static TYPE_CHATGPT = 'chatgpt';

  static TYPE_EXECUTIVESUMMARY = 'executivesummary';
}

export const WidgetDataColumnKeys = Object.freeze({
  FIELD: 'field',
  LABEL: 'label',
});

export const WidgetReportDisplayLength = Object.freeze({
  DEFAULT: 'default',
});

export const WidgetRequestDefaults = Object.freeze({
  NULL: 'null',
});
export class WidgetState extends Enum {
  static STATE_LOADING = 'loading';

  static STATE_BUILDING = 'building';

  static STATE_NEEDS_RELOAD = 'needsReload';

  // There are no records AT ALL in the system (exno campaigns)
  static STATE_NO_RECORDS = 'noRecords';

  // There are records in the system, but none returned for the given filters/dateranges
  static STATE_NO_DATA = 'noData';

  // We have records but they are all null or 0s
  static STATE_ALL_EMPTY_DATA = 'allEmptyData';

  // There may be data but there are no columns to display them with
  static STATE_NO_COLUMNS = 'noColumns';

  // Means the widget is not connected for whoever is in context of the dashboard
  static STATE_INACTIVE = 'inactive';

  static STATE_HAS_ERROR = 'hasError';

  // Too many data to display in the widget
  static STATE_TOO_MANY_DATA = 'tooManyData';

  static STATE_NO_DATA_ACTIONABLE = 'noDataActionable';

  static STATE_INCOMPLETE_BUILDER_CONFIGURATION = 'inCompleteBuilderConfiguration';

  static STATE_DONE = false;

  static STATE_IN_WIDGET_RELOAD = 'inWidgetReload';
}

// Widget rendering constants for dashboard performance optimization
// Note: STATE_LOADING removed so widgets show loading shimmer on initial load
// States that should be hidden when "Show Widgets With No Data" is disabled
export const HIDDEN_WIDGET_STATES = Object.freeze([
  WidgetState.STATE_NO_DATA,
  WidgetState.STATE_ALL_EMPTY_DATA,
  WidgetState.STATE_NO_RECORDS,
]);

export const ALWAYS_VISIBLE_WIDGET_TYPES = Object.freeze([
  WidgetType.TYPE_MEDIA,
  WidgetType.TYPE_CHATGPT,
]);

export class TimeGrouping extends Enum {
  static GROUPING_YEARLY = 'yearly';

  static GROUPING_MONTHLY = 'monthly';

  static GROUPING_WEEKLY = 'weekly';

  static GROUPING_DAILY = 'daily';

  static GROUPING_HOURLY = 'hourly';

  static GROUPING_MONTH_OF_YEAR = 'monthofyear';

  static GROUPING_DAY_OF_MONTH = 'dayofmonth';

  static GROUPING_DAY_OF_WEEK = 'dayofweek';

  static GROUPING_HOUR_OF_DAY = 'hourofday';

  static GROUPING_DYNAMIC = 'dynamic';

  static GROUPING_QUARTERLY = 'quarterly';

  static GROUPING_NON_ISO_WEEK = 'nonisoweek';

  static GROUPING_HOURLY_ADVERTISER = 'hourlystatsadvertiser';

  static GROUPING_HOURLY_AUDIENCE = 'hourlystatsaudience';
}

export class WeeklyStartDay extends Enum {
  static SUNDAY = 'sunday';

  static MONDAY = 'monday';
}

export const DateFormat = {
  DAYS: 'days',
};

// Widget data draw options
export class DrawOption extends Enum {
  // Type the draw options applies to
  static TYPE_ALL = 'widget_all'; // Applies to look and feel of all widgets

  static TYPE_WIDGET = 'widget_type'; // Applies to how data is displayed for that widget type

  static TYPE_REPORT = 'report'; // Applies to how report widgets and other elements are displayed in export builder
  // Draw option location

  static LOCATION_DASHBOARD = 'dashboard';

  static LOCATION_EXPORT = 'export';
  // All widget options

  static SHOW_BACKGROUND = 'show_background';

  static BORDER_COLOR = 'border_color';

  static SHOW_BORDERS = 'show_borders';

  static FONT_COLOR_PICKER = 'font_color_picker';

  static SHOW_SHADOW = 'show_shadow';

  static SHOW_TITLE = 'show_title';

  static SHOW_HEADER = 'show_header';

  static SHOW_DATA_SOURCE = 'show_data_source';

  static SHOW_METRIC_NAME = 'show_metric_name';

  static ENABLE_GRID_THEME = 'grid_theme';

  static SHOW_PAGE_HEADER = 'show_page_header';

  static SHOW_WIDGETS_WITH_NO_DATA = 'show_widgets_with_no_data';

  static SHOW_PAGE_FOOTER = 'show_page_footer';

  static IS_DARK_MODE = 'is_dark_mode';

  static SHOW_COMPARISON_ARROWS = 'show_comparison_arrows';

  static DEFAULT_COMPARISON_ARROW_COLOR = 'default_comparison_arrow_color';
  // Widget type options

  static CIRCLE_SIZE = 'circle_size';

  static FONT_SIZE = 'font_size';

  static REDUCE_NUMBER = 'reduce_number';

  static CENTER_NUMBERS = 'center_numbers';

  static CIRCLE_NUMBER = 'circle_number';

  static PLOT_TYPE = 'plot_type';

  static HAS_LEGEND = 'has_legend';

  static IS_ROTATED = 'is_rotated';

  static HAS_VALUE_SCROLLER = 'has_value_scroller';

  static IS_HAND_DRAWN = 'is_hand_drawn';

  static IS_Y_AXIS_MOVED = 'is_y_axis_moved';

  static IS_NORMALIZED = 'is_normalized';

  static IS_ZERO_SCALED = 'is_zero_scaled';

  static SHOW_LABELS = 'show_labels';

  static SHOW_LABEL_NAMES = 'show_label_names';

  static SHOW_LABEL_VALUES = 'show_label_values';

  static SHOW_LABEL_PERCENT = 'show_label_percent';

  static SHOW_METRIC_LABELS = 'show_metric_labels';

  static OPTION_SHOW_VALUES_ON_SHAPES = 'show_values_on_shapes';

  static DATE_FORMAT_TYPE = 'date_format_type';

  static NECK_HEIGHT = 'neck_height';

  static NECK_WIDTH = 'neck_width';

  static PICTORIAL_OPTIONS = 'v2_pictorial_options';

  static DEPTH = 'depth';

  static ANGLE = 'angle';

  static HIDE_GRID_LINES = 'hide_grid_lines';

  static OTHER_PERCENT = 'other_percent';

  static LABEL_PERCENT = 'label_percent';

  static START_ANGLE = 'start_angle';

  static INNER_RADIUS = 'inner_radius';

  static HAS_BULLETS = 'has_bullets';

  static HAS_TOOLTIP = 'has_tooltip';

  static IS_SMOOTHED_LINE = 'is_smoothed_line';

  static SHOW_SAMPLE_DATA = 'show_sample_data';

  static FORCE_SAMPLE_DATA = 'force_sample_data';

  static SHOW_EMPTY_DATES = 'show_empty_dates';

  static IS_SOLID_GAUGE = 'is_solid_gauge';
  // Datagrid

  static DISPLAY_LENGTH = 'display_length';

  static REPORT_DISPLAY_LENGTH = 'report_display_length';

  static GRID_PAGINATE = 'grid_paginate';

  static GRID_IS_RESPONSIVE = 'grid_is_responsive';

  static GRID_COLLAPSE_IN_MODAL = 'grid_collapse_in_modal';

  static GRID_ALTERNATE_ROW_COLOR = 'grid_alternating_row_color';

  static GRID_MAX_HEIGHT_IMAGE = 'grid_max_height_image';

  static SHOW_TOTAL_ROW = 'show_total_row';

  static GRID_TOTAL_ROW_BOTTOM = 'grid_total_row_bottom';

  static GRID_FONT_SIZE = 'grid_font_size';

  static GRID_FONT_PROPERTIES = 'grid_font_properties';

  static GRID_PERCENT_OF_TOTAL = 'percent_of_total';

  static GRID_FULL_IMAGE_SIZE = 'grid_full_image_size';

  static GRID_PREVIEW_AS_IFRAME = 'grid_preview_as_iframe';

  static GRID_IFRAME_CAPTURE_DELAY = 'grid_iframe_capture_delay';

  static PIVOT_GRID = 'pivot_grid';

  static WRAP_TEXT_NAME = 'wrap_text_name';

  static STYLIZED_EXCEL = 'stylized_excel';

  // Toggle Types

  static TOGGLE_TYPE_SHOW = 'show';

  static TOGGLE_TYPE_HIDE = 'hide';

  static TOGGLE_TYPE_ENABLE = 'enable';

  static TOGGLE_TYPE_DISABLE = 'disable';

  static STATIC_FONT_SIZE = 26;

  static STATIC_CIRCLE_SIZE = 105;

  static Y_AXIS_OVERRIDE = 'y_axis_override';

  static CURVED_COLUMNS = 'curved_columns';
}

export const WidgetBuilderConstants = {
  DATA_TAB_INDEX: 0,
  STYLES_TAB_INDEX: 1,
  LIBRARY_TAB_INDEX: 2,
  NEW_WIDGET_ID: -1, // Temp ID for widget being added
  PREVIEW_CHART: 'preview-chart',
  IS_HIDDEN: 'is-hidden',
};

export const WidgetBuilderPreviewState = {
  COMPLETE_FOR_PREVIEW: false,
  MISSING_DATA_SOURCE: 'missing_data_source',
  MISSING_DATA_VIEW: 'missing_data_view',
  MISSING_SELECTED_METRICS: 'missing_selected_metrics',
  MISSING_GROUPED_METRICS: 'missing_grouped_metrics',
};

export const WidgetBuilderUIEvent = {
  DID_SELECT_WIDGET_TEMPLATE: 'WidgetBuilderUIEvent:did_select_widget_template',
  DID_SELECT_WIDGET_TYPE: 'WidgetBuilderUIEvent:did_select_widget_type',
  CLOSE_PANEL: 'WidgetBuilderUIEvent:close_panel',
  ON_WIDGET_TEMPLATE_SELECT: 'WidgetBuilderUIEvent:on_widget_template_select',
};

export const WidgetBuilderEvent = {
  CANCELLED: 'WidgetBuilderEvent:cancelled',
  DID_SELECT_WIDGET_TYPE: 'WidgetBuilderEvent:did_select_widget_type',
  DID_CREATE_CALCULATION: 'WidgetBuilderEvent:did_create_calculation',
  PRE_SAVE: 'WidgetBuilderEvent:pre_save',
  SAVED: 'WidgetBuilderEvent:saved',
  RELOAD_WIDGET: 'WidgetBuilderEvent:reload_widget',
  STARTED: 'WidgetBuilderEvent:started',
  HOVERED: 'WidgetBuilderEvent:hovered',
  ALERT_UPDATE: 'WidgetBuilderEvent:alert_updated',
  PLOT_TYPE_CHANGED: 'WidgetBuilderEvent:plot_type_changed',
  HIDE_NO_DATA: 'WidgetBuilderEvent:hideNoData',
};

export const WidgetEvent = {
  DUPLICATED: 'WidgetEvent:duplicated',
  CONTENT_REF: 'WidgetEvent:content_ref',
  RELOAD_WIDGET: 'reload-widget',
  RELOAD_CHART: 'reload-chart',
  NO_DATA: 'has-no-data_',
  FOCUS: 'focus',
  BLUR: 'blur',
  UPDATE_DISPLAY_ORDER: 'WidgetEvent:updateDisplayOrder',
  DRAGGED_OVER: 'WidgetEvent:draggedOver',
  APPLY_NO_DATA_FILTER: 'WidgetEvent:applyNoDataFilter',
  LEAD_SAVED: 'WidgetEvent:leadSaved',
  REFRESH_DATA_TABLE: 'WidgetEvent:refreshDataTable',
};

export const MediaWidgetEvent = {
  UPDATE_MEDIA_WIDGET_CONTENT: 'MediaWidgetEvent:update-media-widget-content',
};

export const ComboChartEvent = {
  ON_CHART_ICON_TOGGLE: 'ComboChartEvent:on-chart-icon-toggle',
};

export const WidgetSize = {
  BASE_HEIGHT: 80, // px
  DEFAULT_HEIGHT: 5, // grid units
  MIN_WIDTH: 3, // grid units
  MIN_HEIGHT: 1, // grid units
  MAX_WIDTH: 12, // grid units
  PREVIEW_HEIGHT: 5, // grid units
  MAX_HEIGHT: 14, // grid units
};

export const GridConstants = {
  IGNORE_GRID_BELOW_PAGE_WIDTH: 1170, // px
};

export const PlotType = {
  CLUSTERED: 'clustered',
  LINE: 'line',
  LINE_V2: 'line(v2)',
  STACKED: 'regular',
  FULL_STACKED: '100%',
  DEEP_STACKED: '3d',
  AREA: 'area',
  AREA_V2: 'area(v2)',
  HEAT_MAP: 'heat',
  BUBBLE_MAP: 'bubble',
  HEAT_MAP_V2: 'heat(v2)',
  BUBBLE_MAP_V2: 'bubble(v2)',
  DEFAULT: 'default',
  CLASSIC: 'classic',
  CONDITIONAL_MAP: 'conditional',
  GROUPED_COLUMN: 'grouped_column',
  FUNNEL: 'funnel',
  PYRAMID: 'pyramid',
  STACKED_V2: 'stacked(v2)',
  CLUSTERED_V2: 'clustered(v2)',
  FULL_STACKED_V2: 'full_stacked(v2)',
  RADIAL_HISTOGRAM_V2: 'radial_histogram(v2)',
  RADIAL_BAR_V2: 'radial_bar(v2)',
  LAYERED_V2: 'layered(v2)',
  LOLLIPOP_V2: 'lollipop(v2)',
  GAUGE_V2: 'gauge(v2)',
  MULTI_AXIS_GAUGE: 'gauge_multi_axis(v2)',
  SPARKLINE: 'sparkline',
  EMBEDDED_SPARKLINE: 'embedded_sparklines',
  PICTORIAL: 'pictorial(v2)',
  MAPBOX: 'mapbox',
  DONUT: 'donut(v2)',
  PIE: 'piechart(v2)',
};

export const BenchmarkEnabledPlotTypes = [
  PlotType.LINE_V2,
  PlotType.CLUSTERED_V2,
  PlotType.STACKED_V2,
  PlotType.LAYERED_V2,
  PlotType.LOLLIPOP_V2,
];

export const BenchmarkTypeNames = {
  INSTANCE_BENCHMARKS: 'instance_benchmarks',
  BENCHMARKS: 'benchmarks',
};

export class WidgetDataProbeState extends Enum {
  static IDLE = 'idle';

  static LOADING = 'loading';

  static NO_DATA = 'no_data';

  static SUCCESS = 'success';
}

export const DateFormatType = {
  NO_YEAR: 'noYear',
  DEFAULT: 'default',
};

export const WidgetData = {
  MAX_RESULTS: 1000,
};

export const ApiDataFields = {
  ROW_GROUPING_COUNT: 'row_grouping_count',
  TOTAL_CLIENT_COUNT: 'total_client_count',
};

export const WidgetDatePeriodFormat = {
  YEAR: 'YYYY',
  MONTH: 'MMM YYYY',
  MONTH_WITHOUT_YEAR: 'MMM',
  DATE: 'MMM DD, YYYY',
  DATE_WITHOUT_YEAR: 'MMM DD',
  HOUR: 'hh:mm a MMM DD, YYYY',
  HOUR_WITHOUT_YEAR: 'hh:mm a MMM DD',
  MONTH_OF_YEAR: 'MMMM',
  DAY_OF_MONTH: 'Do',
  DAY_OF_WEEK: 'dddd',
  HOUR_OF_DAY: 'hhA',
};

export const DrawOptionLocation = {
  DASHBOARD: 'dashboard',
  EXPORT: 'export',
};

export class WidgetFilterConditionType extends Enum {
  static EQ = 'eq';

  static GT = 'gt';

  static LT = 'lt';

  static BTW = 'btw';

  static IN = 'in';

  static NIN = 'nin';
}

export class WeekStartOptions extends Enum {
  static SUNDAY = 'sunday';

  static MONDAY = 'monday';
}

export const WidgetFilterConditions = [
  {
    key: WidgetFilterConditionType.GT,
    value: i18n.$t('Greater than or equal to'),
    is_metric: true,
  },
  { key: WidgetFilterConditionType.LT, value: i18n.$t('Less than or equal to'), is_metric: true },
  { key: WidgetFilterConditionType.BTW, value: i18n.$t('In Between'), is_metric: true },
  { key: WidgetFilterConditionType.IN, value: i18n.$t('Include'), is_metric: false },
  { key: WidgetFilterConditionType.NIN, value: i18n.$t('Exclude'), is_metric: false },
];

export const WidgetTimeGroupingWeekStartOptions = [
  { key: WeekStartOptions.MONDAY, value: i18n.$t('Monday') },
  { key: WeekStartOptions.SUNDAY, value: i18n.$t('Sunday') },
];

export const WidgetFilterAssociation = {
  AND: 'and',
  OR: 'or',
};

export const WidgetFilterAssociationLabels = {
  AND: 'AND',
  OR: 'OR',
  INCLUDE: 'Include',
  EXCLUDE: 'Exclude',
};

export const WidgetBuilderTab = {
  DATA_TAB: 0,
  ADVANCED_OPTION_TAB: 1,
  STYLE_TAB: 2,
};

export const WidgetTypeGrouping = {
  DATASOURCED: 'datasourced',
  CHART: 'chart',
  ADMIN: 'admin',
  PREMADE: 'premade',
  SERIES: 'series',
  DISPLAY: 'display',
};

export const WidgetBackgroundColorOptions = {
  DATASOURCE: 'datasource',
  THEME: 'theme',
  CUSTOM: 'custom',
};

export const WidgetBackgroundGradientColorOptions = {
  SOLID: 'solid',
  LINEAR: 'linear',
  LINEAR_Y: 'linear-y',
  RADIAL: 'radial',
};

export const WidgetCursorStyle = {
  POINTER: 'pointer',
  DEFAULT_IMPORTANT: 'default !important',
};

export const WidgetPointerEvent = {
  NONE_IMPORTANT: 'none !important',
};

export const WidgetTemplateEndpoint = {
  DISPLAY: 'display',
  PUBLISH: 'publish',
};

export const WidgetTemplateSearchParam = {
  DATA_SOURCES: 'data_sources',
  TAGS: 'tags',
  WIDGET_TYPES: 'widget_types',
};

export const WidgetTemplateSort = {
  POPULARITY: '-library_download_count,-is_predefined',
};

export const WidgetLogMessage = {
  UNSUPPORTED_TYPE: 'WidgetService.getWidgetTypeByType: Unsupported widget type',
};

export const WidgetColors = {
  FALLBACK_THEME: '#f8f8f8',
};

export const WidgetSampleMarkup = {
  buildImagePlaceholder(index) {
    return `<div class="image-placeholder"><i class="icon icon-picture-o"></i><span>Preview ${index}</span></div>`;
  },
};

export const HandTypes = {
  NEEDLE: 'needle',
  POINTER: 'pointer',
  TRIANGLE: 'triangle',
  NONE: 'none',
  LINE: 'line',
};

export const GradientTargets = {
  EVERYTHING: 'everything',
  HAND: 'hand',
  METRIC: 'metric',
  BAND: 'band',
  HAND_METRIC: 'hand_metric',
  HAND_BAND: 'hand_band',
  METRIC_BAND: 'metric_band',
};

export const WidgetPlotTypeOptions = {
  DEFAULT: 'default',
  MULTI_METRIC: 'multi_metric',
  COMPARISON: 'comparison',
  SPARKLINE: 'sparkline',
  CLASSIC: 'classic', // This is legacy plot type, we may remove this in future
};

export const WidgetComparisonOptions = {
  MONTH_OVER_MONTH: 'month_over_month',
  QUARTER_OVER_QUARTER: 'quarter_over_quarter',
  YEAR_OVER_YEAR: 'year_over_year',
};

export const WidgetSparklineOptions = {
  LAST_3_MONTHS: 'last_3_months',
  LAST_6_MONTHS: 'last_6_months',
  YEAR_TO_DATE: 'year_to_date',
};

export const BenchmarkLineStyle = {
  DASHED: 'dashed',
  DOTTED: 'dotted',
  SOLID: 'solid',
  NONE: 'none',
};

export const WidgetEvents = {
  FRAME_ENDED: 'frame-ended',
};

export const HorizontalAlignments = {
  CENTER: 'center',
  LEFT: 'left',
  RIGHT: 'right',
};

export const WidgetConfig = {
  WIDGET: 'widget',
  STATE: 'state',
};

export const DataGridTotalTooltipMessage = {
  TOOLTIP_TEXT:
    'This total represents an aggregation across all rows based on your widget configuration. These totals can be represented as sums, calculated formulas, most recent values, weighted averages, or even real time requests depending on the source data. If you have questions, please reach out to your support specialists.',
};

export const BigNumberWrapFormat = {
  CIRCLE: 'circle',
  SQUARE: 'square',
};

export const CenterNumbers = {
  CENTER: 'center',
  LEFT: 'left',
  RIGHT: 'right',
};

export const SparkLineTypes = {
  LINE: 'line',
  COLUMN: 'column',
};

export const LineChartLineTypes = {
  LINE: 'line',
  STEP_LINE: 'step_line',
};

export const YAxisPosition = {
  DEFAULT: 'default',
  SWAP: 'swap_y_axes',
  LEFT: 'all_left',
  RIGHT: 'all_right',
};

export const LAYERS_PRIORITY = {
  [LAT_LONG]: 0,
  [COUNTRY]: 1,
  [STATE]: 2,
  [COUNTY]: 3,
  [CITY]: 4,
  [ZIP_CODE]: 5,
  [DMA]: 6,
  [CONGRESSIONAL_DISTRICT]: 7,
};

export const ConditionalType = {
  BETWEEN: 'between',
  GREATER_THAN: 'greater_than',
  LESS_THAN: 'less_than',
};

export const ConditionalTypeRuleValues = {
  MIN: 'min',
  MAX: 'max',
};

export const S_ECHO = 'sEcho';

export const BulletShapeIcons = {
  CIRCLE: 'fa-circle',
  DIAMOND: 'fa-square fa-rotate-45',
  ASTERISK: 'fa-star',
  STAR: 'fa-star',
  TRIANGLE: 'fa-caret-up',
  TRIANGLE_UP: 'fa-caret-up',
  TRIANGLE_DOWN: 'fa-caret-down',
  TRIANGLE_LEFT: 'fa-caret-left',
  TRIANGLE_RIGHT: 'fa-caret-right',
  SQUARE: 'fa-square',
};

export const BulletShapes = {
  CIRCLE: 'circle',
  DIAMOND: 'diamond',
  STAR: 'star',
  TRIANGLE: 'triangle',
  TRIANGLE_DOWN: 'triangle-down',
  TRIANGLE_LEFT: 'triangle-left',
  TRIANGLE_RIGHT: 'triangle-right',
  SQUARE: 'square',
};
export const WidgetFields = {
  NAME: 'name',
};

export const WIDGET_TYPE_CHANGE = 'widget_type_change';
export const FIELD_LOG_DATE = 'log_date';
export const FIELD_FORMATTED_LOG_DATE = 'formatted_log_date';
export const MAIN_DATA_VIEW = 'cgn';

export const PIN = 'pin';
export const FilterDateRangeValues = {
  CUSTOM: 'custom',
  RELATIVE: 'relative',
};

export const KEY_LOG_DATE = '$log_date';
export const KEY_LOG_DATE_COMPARISON = '$log_date_comparison';

export function GetMediaWidgetEditorOptions() {
  return {
    focus: true,
    dialogsFade: true,
    dialogsInBody: true,
    disableResizeEditor: true,
    placeholder: i18n.$t('Please enter text in order to save Media Widget'),
    codeviewFilter: false,
    toolbar: [
      ['headline', ['style']],
      ['fontface', ['fontname']],
      ['textsize', ['fontsize']],
      ['color', ['color']],
      ['style', ['bold', 'italic', 'underline', 'clear']],
      ['alignment', ['ul', 'ol', 'paragraph', 'lineheight']],
      ['height', ['height']],
      ['table', ['table']],
      ['insert', ['link', 'picture', 'video']],
      ['view', ['fullscreen', 'codeview']],
      ['edit', ['undo', 'redo']],
      ['help', ['help']],
    ],
    maximumImageFileSize: 5242880,
    fontNames: [
      'Arial',
      'Arial Black',
      'Comic Sans MS',
      'Courier New',
      'Helvetica Neue',
      'Helvetica',
      'Impact',
      'Lato',
      'Lucida Grande',
      'Tahoma',
      'Times New Roman',
      'Verdana',
      'Mulish',
    ],
  };
}

export function GetWidgetTitleEditorOptions() {
  return {
    minHeight: '100%',
    focus: false,
    dialogsFade: true,
    dialogsInBody: true,
    disableResizeEditor: true,
    placeholder: i18n.$t('Please enter text in order to edit Widget Header'),
    toolbar: [
      ['headline', ['style']],
      ['fontface', ['fontname']],
      ['textsize', ['fontsize']],
      ['color', ['color']],
      ['style', ['bold', 'italic', 'underline', 'clear']],
      ['alignment', ['paragraph', 'lineheight']],
      ['height', ['height']],
      ['view', ['codeview']],
      ['edit', ['undo', 'redo']],
      ['help', ['help']],
    ],
    styleTags: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    maximumImageFileSize: 5242880,
    codeviewFilter: false,
    maxTextLength: 600,
    fontNames: [
      'Arial',
      'Arial Black',
      'Comic Sans MS',
      'Courier New',
      'Helvetica Neue',
      'Helvetica',
      'Impact',
      'Lato',
      'Lucida Grande',
      'Tahoma',
      'Times New Roman',
      'Verdana',
      'Mulish',
    ],
    fontNamesIgnoreCheck: [
      'Arial',
      'Arial Black',
      'Comic Sans MS',
      'Courier New',
      'Helvetica Neue',
      'Helvetica',
      'Impact',
      'Lato',
      'Lucida Grande',
      'Tahoma',
      'Times New Roman',
      'Verdana',
      'Mulish',
    ],
  };
}

export const WidgetDataViews = {
  DATA_VIEW_AGE: 'Age',
  DATA_VIEW_GENDER_AGES: 'Gender & Ages',
  DATA_VIEW_DEMOGRAPHICS_BREAKDOWN: 'Demographics Breakdown',
};

export const LineChartFullStackedLocationX = [
  [10, 1],
  [20, 1.2],
  [30, 1.4],
  [40, 1.8],
  [50, 2.2],
  [60, 2.4],
  [70, 3],
  [80, 3.4],
  [90, 3.6],
  [100, 4],
  [160, 7.5],
  [200, 7.6],
  [300, 11],
];

export const LineChartLocationX = [
  [10, 1],
  [20, 1.2],
  [30, 1.4],
  [40, 1.6],
  [50, 1.8],
  [60, 2],
  [70, 2.2],
  [80, 2.4],
  [90, 2.6],
  [100, 2.8],
  [200, 4.8],
  [300, 7],
];

export const STATUS_FAILURE = 'Failure';

export const ID = 'id';

export const DISTINCT_VALUES = 'values/distinct';

export const TIME_UNIT_GROUPINGS = [
  TimeGrouping.GROUPING_MONTH_OF_YEAR,
  TimeGrouping.GROUPING_DAY_OF_WEEK,
  TimeGrouping.GROUPING_DAY_OF_MONTH,
  TimeGrouping.GROUPING_HOUR_OF_DAY,
  TimeGrouping.GROUPING_YEARLY,
  TimeGrouping.GROUPING_HOURLY,
  TimeGrouping.GROUPING_HOURLY_AUDIENCE,
  TimeGrouping.GROUPING_HOURLY_ADVERTISER,
  TimeGrouping.GROUPING_DYNAMIC,
];
